import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Badge,
  Card,
  CardBody,
  Row,
  Col,
  Spinner,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { getTagReports, suspendHotspot } from "./reportedHotspotApi";
import EditReportModal from "./editReportModal";
import BatchUpdateModal from "./batchUpdateModal";
import SuspendModal from "../../hotspotModeration/helper/suspendModal";
import UserInfoModal from "../../../components/Common/UserInfoModal";

const ReportsModal = ({ isOpen, toggle, tagId, tagName, onUpdate }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [batchUpdateModal, setBatchUpdateModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [selectedReports, setSelectedReports] = useState(new Set());
  const [stateFilter, setStateFilter] = useState("");
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  const limit = 50;

  const fetchReports = useCallback(async (page = 1, append = false) => {
    if (!tagId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setLoading(true);
    try {
      const result = await getTagReports(tagId, { limit, page });
      
      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      if (append) {
        setReports((prev) => [...prev, ...result.reports]);
      } else {
        setReports(result.reports);
      }

      setCurrentPage(result.pagination.currentPage);
      setTotalPages(result.pagination.totalPages);
      setTotalRecords(result.pagination.totalRecords);
      setHasMore(result.pagination.currentPage < result.pagination.totalPages);
    } catch (error) {
      if (error.name !== 'AbortError' && isMountedRef.current) {
        console.error("Error fetching reports:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [tagId, limit]);

  useEffect(() => {
    isMountedRef.current = true;
    if (isOpen && tagId) {
      setCurrentPage(1);
      setReports([]);
      fetchReports(1, false);
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isOpen, tagId, fetchReports]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchReports(currentPage + 1, true);
    }
  };

  const getStateBadge = (state) => {
    switch (state?.toLowerCase()) {
      case "resolved":
        return <Badge color="success" className="px-2 py-1">Resolved</Badge>;
      case "raised":
        return <Badge color="warning" className="px-2 py-1">Raised</Badge>;
      case "inprogress":
      case "in_progress":
      case "in progress":
        return <Badge color="info" className="px-2 py-1">In Progress</Badge>;
      case "none":
        return <Badge color="secondary" className="px-2 py-1">None</Badge>;
      default:
        return <Badge color="secondary" className="px-2 py-1">{state || "None"}</Badge>;
    }
  };

  const handleEditClick = (report) => {
    setSelectedReport(report);
    setEditModal(true);
  };

  const handleEditUpdate = useCallback(() => {
    // Refresh the current page to get updated data
    if (tagId) {
      fetchReports(currentPage, false);
      setSelectedReports(new Set()); // Clear selections after update
    }
  }, [tagId, currentPage, fetchReports]);

  // Filter reports based on state
  const filteredReports = reports.filter(report => {
    if (!stateFilter) return true;
    // Normalize both values for comparison
    const reportState = report.state?.toLowerCase().replace(/_/g, '').replace(/\s/g, '');
    const filterState = stateFilter.toLowerCase().replace(/_/g, '').replace(/\s/g, '');
    return reportState === filterState;
  });

  // Handle individual report selection
  const handleReportSelect = (reportId) => {
    setSelectedReports(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  // Handle select all filtered reports
  const handleSelectAll = () => {
    if (selectedReports.size === filteredReports.length && filteredReports.length > 0) {
      // Deselect all
      setSelectedReports(new Set());
    } else {
      // Select all filtered
      const allIds = new Set(filteredReports.map(r => r.report_id));
      setSelectedReports(allIds);
    }
  };

  // Get selected report objects
  const getSelectedReportObjects = () => {
    return reports.filter(r => selectedReports.has(r.report_id));
  };

  // Handle batch update
  const handleBatchUpdate = () => {
    const selected = getSelectedReportObjects();
    if (selected.length === 0) {
      alert("Please select at least one report");
      return;
    }
    setBatchUpdateModal(true);
  };

  const handleBatchUpdateComplete = () => {
    setSelectedReports(new Set());
    handleEditUpdate();
  };

  const handleSuspendClick = () => {
    setSuspendModal(true);
  };

  const handleSuspendConfirm = useCallback(async (reason) => {
    if (!tagId) {
      throw new Error("Tag ID is required for suspension");
    }

    try {
      await suspendHotspot({
        tag_id: tagId,
        reason: reason,
      });

      // Close modal and refresh if callback provided
      setSuspendModal(false);
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Error suspending hotspot:", error);
      throw error;
    }
  }, [tagId, onUpdate]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleViewUserInfo = useCallback((userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleClose = () => {
    setReports([]);
    setCurrentPage(1);
    setSelectedReports(new Set());
    setStateFilter("");
    setUserInfoModal(false);
    setSelectedUserId(null);
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg" className="reports-modal">
      <ModalHeader className="bg-danger text-white" toggle={handleClose}>
        <div className="d-flex align-items-center">
          <i className="mdi mdi-flag me-2"></i>
          <span>Reports for Tag #{tagId} - {tagName}</span>
        </div>
      </ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }} className="p-3">
        {loading && reports.length === 0 ? (
          <div className="text-center py-5">
            <Spinner color="danger" />
            <p className="mt-3 text-muted">Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-5">
            <i className="mdi mdi-information-outline" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
            <p className="mt-3 text-muted">No reports found for this tag.</p>
          </div>
        ) : (
          <>
            <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div>
                <Badge color="info" className="px-3 py-2 me-2">
                  Total Reports: {totalRecords}
                </Badge>
                {filteredReports.length !== reports.length && (
                  <Badge color="secondary" className="px-3 py-2">
                    Filtered: {filteredReports.length}
                  </Badge>
                )}
                {selectedReports.size > 0 && (
                  <Badge color="primary" className="px-3 py-2">
                    Selected: {selectedReports.size}
                  </Badge>
                )}
              </div>
              <div className="d-flex gap-2 align-items-center">
                <FormGroup className="mb-0" style={{ minWidth: "150px" }}>
                  <Input
                    type="select"
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    size="sm"
                  >
                    <option value="">All States</option>
                    <option value="raised">Raised</option>
                    <option value="inProgress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </Input>
                </FormGroup>
                {filteredReports.length > 0 && (
                  <Button
                    color="outline-primary"
                    size="sm"
                    onClick={handleSelectAll}
                  >
                    {selectedReports.size === filteredReports.length ? "Deselect All" : "Select All"}
                  </Button>
                )}
                {selectedReports.size > 0 && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={handleBatchUpdate}
                  >
                    <i className="mdi mdi-checkbox-multiple-marked me-1"></i>
                    Batch Update ({selectedReports.size})
                  </Button>
                )}
              </div>
            </div>
            {filteredReports.length === 0 ? (
              <div className="text-center py-4">
                <i className="mdi mdi-filter-remove" style={{ fontSize: "2rem", color: "#6c757d" }}></i>
                <p className="mt-2 text-muted">No reports match the selected filter</p>
              </div>
            ) : (
              filteredReports.map((report, index) => (
              <Card 
                key={report.report_id || index} 
                className={`mb-3 shadow-sm border-left-danger ${selectedReports.has(report.report_id) ? 'border-primary border-2' : ''}`} 
                style={{ borderLeftWidth: "4px" }}
              >
                <CardBody className="p-3">
                  <Row className="g-2">
                    <Col xs={12}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <Input
                            type="checkbox"
                            checked={selectedReports.has(report.report_id)}
                            onChange={() => handleReportSelect(report.report_id)}
                            className="form-check-input"
                            style={{ cursor: "pointer" }}
                          />
                          <div>
                            <Badge color="danger" className="px-2 py-1 me-2">
                              <i className="mdi mdi-tag me-1"></i>{report.category}
                            </Badge>
                            {getStateBadge(report.state)}
                          </div>
                        </div>
                        <div className="text-muted small">
                          <i className="mdi mdi-clock-outline me-1"></i>
                          {formatDate(report.reported_at)}
                        </div>
                      </div>
                    </Col>
                    
                    {report.reason && (
                      <Col xs={12}>
                        <div className="mb-2">
                          <label className="form-label text-muted small mb-1 fw-bold">
                            <i className="mdi mdi-message-text me-1"></i>Reason
                          </label>
                          <div className="p-2 bg-light rounded border" style={{ whiteSpace: "pre-wrap" }}>
                            {report.reason}
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">
                          <i className="mdi mdi-account-alert me-1"></i>Reporter
                        </label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="flex-grow-1">
                            <div className="fw-semibold">
                              {report.reporter_username || report.reporter_user_id || (
                                <span className="text-muted">Anonymous</span>
                              )}
                            </div>
                            {report.reporter_user_id && (
                              <div className="text-muted small font-monospace">{report.reporter_user_id}</div>
                            )}
                          </div>
                          {report.reporter_user_id && (
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handleViewUserInfo(report.reporter_user_id)}
                              className="px-2"
                              title="View Reporter Details"
                            >
                              <i className="mdi mdi-eye"></i>
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="mb-2">
                        <label className="form-label text-muted small mb-1">
                          <i className="mdi mdi-account me-1"></i>Reported User
                        </label>
                        <div className="d-flex align-items-center gap-2">
                          <div className="flex-grow-1">
                            <div className="fw-semibold">{report.reported_username || "N/A"}</div>
                            {report.reported_user_id && (
                              <div className="text-muted small font-monospace">{report.reported_user_id}</div>
                            )}
                          </div>
                          {report.reported_user_id && (
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handleViewUserInfo(report.reported_user_id)}
                              className="px-2"
                              title="View Reported User Details"
                            >
                              <i className="mdi mdi-eye"></i>
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>

                    {report.admin_note && (
                      <Col xs={12}>
                        <div className="mb-2">
                          <label className="form-label text-muted small mb-1 fw-bold">
                            <i className="mdi mdi-note-text me-1"></i>Admin Note
                          </label>
                          <div className="p-2 bg-info-subtle rounded border border-info">
                            {report.admin_note}
                          </div>
                        </div>
                      </Col>
                    )}

                    <Col xs={12}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small font-monospace">Report ID: {report.report_id}</span>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleEditClick(report)}
                          className="ms-2"
                        >
                          <i className="mdi mdi-pencil me-1"></i>Edit
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              ))
            )}

            {hasMore && (
              <div className="text-center mt-3">
                <Button
                  color="primary"
                  outline
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <i className="mdi mdi-arrow-down me-2"></i>
                      Load More ({totalRecords - reports.length} remaining)
                    </>
                  )}
                </Button>
              </div>
            )}

            {!hasMore && reports.length > 0 && (
              <div className="text-center mt-3">
                <Badge color="secondary" className="px-3 py-2">
                  All reports loaded
                </Badge>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter className="bg-light">
        {tagId && (
          <Button 
            color="warning" 
            onClick={handleSuspendClick}
            className="px-4 me-2"
          >
            <i className="mdi mdi-pause-circle me-2"></i>Suspend Hotspot
          </Button>
        )}
        <Button color="secondary" onClick={handleClose} className="px-4">
          <i className="mdi mdi-close me-2"></i>Close
        </Button>
      </ModalFooter>

      <EditReportModal
        isOpen={editModal}
        toggle={() => {
          setEditModal(false);
          setSelectedReport(null);
        }}
        report={selectedReport}
        onUpdate={handleEditUpdate}
      />

      <BatchUpdateModal
        isOpen={batchUpdateModal}
        toggle={() => {
          setBatchUpdateModal(false);
        }}
        selectedReports={getSelectedReportObjects()}
        onUpdate={handleBatchUpdateComplete}
      />

      <SuspendModal
        isOpen={suspendModal}
        toggle={() => {
          setSuspendModal(false);
        }}
        tagId={tagId}
        tagName={tagName}
        onConfirm={handleSuspendConfirm}
      />

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh reports after user action
          if (tagId) {
            fetchReports(currentPage, false);
          }
        }}
      />
    </Modal>
  );
};

export default ReportsModal;

